import { c as _c } from "react/compiler-runtime";
import { Button } from '@systemeio/components';
import { EVENT_ITEM_CLASS_NAME, EVENT_LIST_CLASS_NAME, EventSkeletonItem, EventsSkeleton } from 'modules/main/components/event-box-section/events-skeleton';
import RenderEvent from 'modules/main/components/render-event';
import { useMainEvents } from 'modules/main/hooks/use-main-events';
import { useEffect, useRef } from 'react';
import { EmptyState } from 'shared/components/empty-states';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twJoin } from 'tailwind-merge';

/*
 * On desktop size containment keeps the card from adding to the grid row, so the charts beside it
 * alone decide the row height, the card stretches to it and its list scrolls inside; below desktop
 * the list is part of the page, and `min-w-0` keeps its truncated lines from widening the page's
 * grid column to the longest of them. The sentinel is observed against the viewport in both cases:
 * an intersection is clipped by the scrolling list too, so it only shows up at its end.
 */
export const EventBoxSection = () => {
  const $ = _c(34);
  const {
    t
  } = useLocoTranslation();
  const {
    events,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    retry
  } = useMainEvents();
  const sentinelRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== error || $[1] !== hasMore || $[2] !== isLoadingMore || $[3] !== loadMore) {
    t0 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel || !hasMore || isLoadingMore || error) {
        return;
      }
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        if (entry?.isIntersecting) {
          loadMore();
        }
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    t1 = [error, hasMore, isLoadingMore, loadMore];
    $[0] = error;
    $[1] = hasMore;
    $[2] = isLoadingMore;
    $[3] = loadMore;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[6] !== error || $[7] !== events || $[8] !== hasMore || $[9] !== isLoadingMore || $[10] !== retry || $[11] !== t) {
    const renderContent = () => {
      if (!events) {
        return error ? <EmptyState className="py-10" title={t("core.error.internal_error_message")} /> : <EventsSkeleton />;
      }
      if (!events.length) {
        return <EmptyState className="py-10" title={t("dashboard.main.event.empty.title")} />;
      }
      return <><ul className={EVENT_LIST_CLASS_NAME}>{events.map(_temp)}{isLoadingMore && <li className={EVENT_ITEM_CLASS_NAME}><EventSkeletonItem /></li>}{error && !isLoadingMore && <li className={twJoin(EVENT_ITEM_CLASS_NAME, "flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center")}><span className="text-text-secondary">{t("core.error.internal_error_message")}</span><Button variant="link" onClick={retry}>{t("dashboard.main.event.retry")}</Button></li>}</ul>{hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px shrink-0" />}</>;
    };
    t5 = "border-border-100 bg-bg-primary desktop:contain-size flex min-w-0 flex-col rounded-lg border";
    let t7;
    if ($[17] !== t) {
      t7 = t("dashboard.main.event.title");
      $[17] = t;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    if ($[19] !== t7) {
      t6 = <h2 className="text-text-primary px-6 pt-5 text-lg font-medium">{t7}</h2>;
      $[19] = t7;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t4 = "relative flex min-h-0 flex-1 flex-col";
    t2 = "desktop:overflow-y-auto flex min-h-0 flex-1 flex-col px-6 pt-5 pb-5";
    t3 = renderContent();
    $[6] = error;
    $[7] = events;
    $[8] = hasMore;
    $[9] = isLoadingMore;
    $[10] = retry;
    $[11] = t;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[21] !== t2 || $[22] !== t3) {
    t7 = <div className={t2}>{t3}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== events?.length) {
    t8 = !!events?.length && <div aria-hidden="true" className="to-bg-primary desktop:block pointer-events-none absolute inset-x-0 bottom-0 hidden h-10 rounded-b-lg bg-linear-to-b from-transparent" />;
    $[24] = events?.length;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t4 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className={t4}>{t7}{t8}</div>;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t5 || $[31] !== t6 || $[32] !== t9) {
    t10 = <section className={t5}>{t6}{t9}</section>;
    $[30] = t5;
    $[31] = t6;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
};
function _temp(event) {
  return <li key={event.id} className={EVENT_ITEM_CLASS_NAME}><RenderEvent event={event} /></li>;
}